import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { CategoryChip } from '@/components/CategoryChip';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { RECURRING_GROUP_ORDER, RECURRING_TAXONOMY, useRecurringTaxonomyColors } from './taxonomy';
import type { RecurringGroupKey, RecurringPayment } from '@/lib/api';

export interface TaggedRecurring {
  item: RecurringPayment;
  tipo: RecurringGroupKey;
}

interface Props {
  /** All recurrences (parcelamentos + assinaturas + contas fixas). */
  entries: TaggedRecurring[];
}

export function CategoryBreakdown({ entries }: Props) {
  const formatCurrency = useFormatCurrency();
  const taxonomyColors = useRecurringTaxonomyColors();

  const { rows, grand } = useMemo(() => {
    const map = new Map<string, { category: string; categoryKey: string; iconName: string | null; total: number; count: number; byTipo: Record<string, number> }>();
    for (const { item, tipo } of entries) {
      const category = item.categoryTranslated ?? item.category ?? 'Outros';
      const existing = map.get(category) ?? {
        category,
        categoryKey: item.categoryId ?? item.category ?? category,
        iconName: item.categoryIcon ?? item.parentCategoryIcon ?? null,
        total: 0,
        count: 0,
        byTipo: { parcelamentos: 0, assinaturas: 0, fixas: 0 },
      };
      const amount = Math.abs(item.amount);
      existing.total += amount;
      existing.count += 1;
      existing.byTipo[tipo] += amount;
      map.set(category, existing);
    }
    const rows = [...map.values()].sort((a, b) => b.total - a.total);
    const grand = rows.reduce((sum, r) => sum + r.total, 0);
    return { rows, grand };
  }, [entries]);

  if (rows.length === 0) {
    return (
      <Card className="border-dashed p-8 text-center text-sm text-text-secondary">
        Nenhuma recorrência categorizada ainda.
      </Card>
    );
  }

  return (
    <Card className="p-5 sm:p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-text-primary">Gastos por categoria</h2>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {RECURRING_GROUP_ORDER.map((key) => (
            <span key={key} className="flex items-center gap-1.5 text-xs text-text-secondary">
              <span className="size-2 rounded-full" style={{ backgroundColor: taxonomyColors[key] }} />
              {RECURRING_TAXONOMY[key].label}
            </span>
          ))}
        </div>
      </div>

      <ul className="space-y-4" role="list">
        {rows.map((row) => {
          const share = grand > 0 ? row.total / grand : 0;
          return (
            <li key={row.category}>
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2">
                  <CategoryChip
                    categoryKey={row.categoryKey}
                    label={row.category}
                    iconName={row.iconName}
                    title={row.category}
                    iconOnly
                    size="sm"
                  />
                  <span className="min-w-0 truncate text-sm font-medium text-text-primary">
                    {row.category}
                    <span className="ml-2 text-2xs font-normal text-text-tertiary">
                      {row.count} {row.count === 1 ? 'recorrência' : 'recorrências'}
                    </span>
                  </span>
                </span>
                <span className="flex shrink-0 items-baseline gap-2">
                  <span className="font-mono text-sm text-text-primary tabular-nums">{formatCurrency(row.total)}</span>
                  <span className="w-9 text-right font-mono text-2xs text-text-tertiary tabular-nums">
                    {(share * 100).toFixed(0)}%
                  </span>
                </span>
              </div>
              <div className="flex h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
                {RECURRING_GROUP_ORDER.map((key) =>
                  row.byTipo[key] > 0 ? (
                    <span
                      key={key}
                      className="h-full"
                      style={{ width: `${grand > 0 ? (row.byTipo[key] / grand) * 100 : 0}%`, backgroundColor: taxonomyColors[key] }}
                    />
                  ) : null,
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <p className="mt-6 flex items-center justify-between border-t border-border-subtle pt-4 text-xs text-text-tertiary">
        <span>Total mensal recorrente</span>
        <span className="font-mono text-sm text-text-primary tabular-nums">{formatCurrency(grand)}</span>
      </p>
    </Card>
  );
}
